import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import 'prismjs/themes/prism.css';
import { applyAppViewportCssVars } from './styles/brandPalette';

/* Set zoom/sidebar CSS vars before React mounts to avoid a first-paint "zoomed in" flash. */
applyAppViewportCssVars(true);

// Suppress ResizeObserver loop errors globally
// These are benign errors that occur when ResizeObserver callbacks trigger resizes
// This is a known browser behavior and doesn't affect functionality
// Monaco Editor and other libraries use ResizeObserver internally
//
// 1) Patch ResizeObserver to defer callbacks and avoid the loop (prevents error at source)
// 2) Catch any remaining ResizeObserver errors so they don't show the overlay
//
// IMPORTANT: This code runs FIRST, before React loads, to catch errors early

const isResizeObserverError = (message: string | undefined | null): boolean => {
  if (!message) return false;
  const msg = message.toLowerCase();
  return (
    msg.includes('resizeobserver') ||
    msg.includes('resize observer') ||
    msg.includes('loop completed') ||
    msg.includes('undelivered notifications') ||
    msg.includes('resizeobserver loop')
  );
};

if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends NativeResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (e) {
            if (!isResizeObserverError((e as Error)?.message)) throw e;
          }
        });
      });
    }
  };
}

// Function to remove error overlay elements immediately
const removeResizeObserverErrorOverlay = () => {
  try {
    // Look for error overlay elements in the DOM
    const selectors = [
      '[class*="error-overlay"]',
      '[id*="error-overlay"]',
      '[class*="react-error"]',
      '[id*="react-error"]',
      'iframe[src*="error"]'
    ];
    
    selectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          const text = element.textContent || '';
          if (isResizeObserverError(text)) {
            element.remove();
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
          }
        });
      } catch (e) {
        // Ignore selector errors
      }
    });
    
    // Check all fixed position elements with high z-index (error overlays)
    try {
      const allElements = document.querySelectorAll('*');
      allElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          const style = window.getComputedStyle(element);
          const text = element.textContent || '';
          if (
            style.position === 'fixed' &&
            (style.zIndex === '2147483647' || parseInt(style.zIndex) > 10000) &&
            isResizeObserverError(text)
          ) {
            element.remove();
          }
        }
      });
    } catch (e) {
      // Ignore errors
    }
  } catch (e) {
    // Ignore all errors in removal function
  }
};

// Store original handlers before any overrides
const originalError = console.error;
const originalWarn = console.warn;

// Suppress in console.error (MUST be first to catch early)
console.error = (...args: any[]) => {
  const first = args[0];
  const errorMessage =
    (first?.message ?? first?.toString?.() ?? (typeof first === 'string' ? first : '')) ||
    args.map((a) => a?.message ?? a?.toString?.()).filter(Boolean).join(' ');
  if (isResizeObserverError(errorMessage)) {
    // Suppress ResizeObserver errors - they're benign
    return;
  }
  originalError.apply(console, args);
};

// Also suppress in console.warn (some browsers log ResizeObserver as warnings)
console.warn = (...args: any[]) => {
  const warningMessage = args[0]?.toString() || args[0]?.message || args[0] || '';
  if (isResizeObserverError(warningMessage)) {
    // Suppress ResizeObserver warnings - they're benign
    return;
  }
  originalWarn.apply(console, args);
};

// Suppress in window error handler (catches runtime errors) - must run first (capture)
window.addEventListener('error', (event) => {
  const msg = (event.message ?? event.error?.message ?? '').toString();
  if (isResizeObserverError(msg)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    (event as any).returnValue = false;
    return false;
  }
}, true);

// Also handle in bubble phase so we run before overlay if it uses capture
window.addEventListener('error', (event) => {
  const msg = (event.message ?? event.error?.message ?? '').toString();
  if (isResizeObserverError(msg)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    (event as any).returnValue = false;
    return false;
  }
}, false);

// Suppress unhandled promise rejections related to ResizeObserver
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const errorMessage = reason?.message || reason?.toString() || '';
  if (isResizeObserverError(errorMessage)) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Suppress React error overlay for ResizeObserver errors
// This catches errors that React's error boundary would show
const originalOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (isResizeObserverError(message?.toString() || error?.message)) {
    return true; // Suppress the error
  }
  if (originalOnError) {
    return originalOnError(message, source, lineno, colno, error);
  }
  return false;
};

// Suppress React development error overlay for ResizeObserver errors
// This is the overlay that shows "Uncaught runtime errors" in development
// Run this immediately to catch errors before React's error overlay initializes
(function suppressReactErrorOverlay() {
  try {
    // Method 1: Intercept react-error-overlay's global handler
    const windowWithOverlay = window as typeof window & {
      __REACT_ERROR_OVERLAY_GLOBAL_HANDLER__?: (error: Error, isFatal: boolean) => void;
    };
    
    if (windowWithOverlay.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__) {
      const originalHandler = windowWithOverlay.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__;
      windowWithOverlay.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ = (error: Error, isFatal: boolean) => {
        if (isResizeObserverError(error.message)) {
          return; // Suppress ResizeObserver errors
        }
        originalHandler(error, isFatal);
      };
    }
    
    // Method 2: Intercept window.reportError (modern error reporting API)
    const originalReportError = (window as any).reportError;
    if (typeof originalReportError === 'function') {
      (window as any).reportError = function(error: Error) {
        if (isResizeObserverError(error?.message)) {
          return; // Suppress ResizeObserver errors
        }
        originalReportError.call(this, error);
      };
    }
    
    // Method 3: Intercept error events before they reach the overlay
    const originalDispatchEvent = window.dispatchEvent;
    window.dispatchEvent = function(event: Event) {
      if (event.type === 'error' && event instanceof ErrorEvent) {
        if (isResizeObserverError(event.message)) {
          event.stopImmediatePropagation();
          event.preventDefault();
          return false;
        }
      }
      return originalDispatchEvent.call(this, event);
    };
    
    // Method 4: Try to find and override the error overlay's internal error handler
    // This targets the actual error overlay component that shows the red banner
    const checkAndOverrideOverlay = () => {
      try {
        // Look for the error overlay's error handler in the DOM or global scope
        const overlayScript = document.querySelector('script[src*="react-error-overlay"]');
        if (overlayScript) {
          // Error overlay is loaded, try to find its handler
          const overlayWindow = window as any;
          if (overlayWindow.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__) {
            const handler = overlayWindow.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__;
            overlayWindow.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ = (error: Error, isFatal: boolean) => {
              if (isResizeObserverError(error?.message)) {
                return;
              }
              handler(error, isFatal);
            };
          }
        }
      } catch (e) {
        // Ignore errors in overlay detection
      }
    };
    
    // Check immediately and also after a delay (in case overlay loads later)
    checkAndOverrideOverlay();
    setTimeout(checkAndOverrideOverlay, 100);
    setTimeout(checkAndOverrideOverlay, 500);
    setTimeout(checkAndOverrideOverlay, 1000);
    
    // Method 5: Watch for error overlay DOM elements and remove them if they show ResizeObserver errors
    // Use the global function defined above
    // Run immediately and continuously
    removeResizeObserverErrorOverlay();
    
    // Set up MutationObserver to watch for error overlay additions
    const observer = new MutationObserver(() => {
      removeResizeObserverErrorOverlay();
    });
    
    // Start observing immediately (even before DOM is ready)
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
    } else {
      // Wait for body to be available
      const bodyObserver = new MutationObserver(() => {
        if (document.body) {
          observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false
          });
          bodyObserver.disconnect();
        }
      });
      bodyObserver.observe(document.documentElement, {
        childList: true,
        subtree: false
      });
    }
    
    // Fallback: run periodically to catch overlays MutationObserver might miss (2s is sufficient; 50ms was excessive and caused high CPU)
    setInterval(removeResizeObserverErrorOverlay, 2000);
    
  } catch (e) {
    // Ignore errors in error suppression setup
  }
})();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
); 